import { MONTHS, ROUTE_DOCS_MONITORING } from '@/common/constants';
import { useSidebar } from '@/components/ui/sidebar';
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ICottonData,
  IDataRepublic,
} from '@/features/monitoring/models/IMonitoring';
import { cn } from '@/lib/utils';
import React, { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import useTable from '../../republic/table/useTable';

type Props = {
  dataDistrict: IDataRepublic[];
  cottonData: ICottonData[];
};

export const TableDistrict: FC<Props> = ({ dataDistrict = [], cottonData }) => {
  const { t } = useTranslation();
  const { open } = useSidebar();
  const { lang, type, region_id } = useParams();
  const navigate = useNavigate();
  const { getActionsByMonth, allMonths, getValue } = useTable();
  const [searchParams] = useSearchParams();
  const plantId = searchParams.get('plant_id') as string;
  const year = searchParams.get('year') as string;

  const headerScrollRef = useRef<HTMLDivElement>(null);
  const bodyScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const headerElement = headerScrollRef.current;
    const bodyElement = bodyScrollRef.current;

    if (!headerElement || !bodyElement) return;

    const handleBodyScroll = () => {
      if (headerElement.scrollLeft !== bodyElement.scrollLeft) {
        headerElement.scrollLeft = bodyElement.scrollLeft;
      }
    };

    const handleHeaderScroll = () => {
      if (bodyElement.scrollLeft !== headerElement.scrollLeft) {
        bodyElement.scrollLeft = headerElement.scrollLeft;
      }
    };

    bodyElement.addEventListener('scroll', handleBodyScroll);
    headerElement.addEventListener('scroll', handleHeaderScroll);

    return () => {
      bodyElement.removeEventListener('scroll', handleBodyScroll);
      headerElement.removeEventListener('scroll', handleHeaderScroll);
    };
  }, []);

  const getCottonByDistrictSoato = (districtSoato?: number) => {
    return cottonData?.find((el) => el.district_soato === districtSoato);
  };

  return (
    <div className="h-full w-full rounded-10 border bg-white overflow-hidden flex flex-col">
      {/* Fixed Header Table */}
      <div
        ref={headerScrollRef}
        className={cn(
          'flex-shrink-0 border-b overflow-x-auto overflow-y-hidden scrollbar-hide',
          open ? 'w-[calc(100vw-320px)]' : 'w-[calc(100vw-150px)]',
        )}
      >
        <table
          className="w-full caption-bottom text-sm select-none border-collapse table-fixed"
          style={{ width: 'max-content', minWidth: '100%' }}
        >
          <TableHeader>
            <TableRow className="bg-gray-100 hover:bg-gray-100">
              <TableHead className="h-10 w-[250px] text-[12px] font-medium text-black/60 border-b py-3 text-left px-4"></TableHead>
              {dataDistrict?.map((district) => (
                <TableHead
                  key={district?.id}
                  className="h-10 w-[140px] text-[12px] text-center font-medium text-black/60 border-b py-3 !px-1"
                  onClick={() =>
                    navigate({
                      pathname: `/${lang}/${ROUTE_DOCS_MONITORING}/${type || 'COTTON'}/${region_id}/${district?.id}`,
                      search: window.location.search,
                    })
                  }
                >
                  <div className="w-full flex justify-center">
                    <p className=" text-sm font-medium text-dark-blue text-center hover:text-cyan-default cursor-pointer duration-300">
                      {district?.name_uz}
                    </p>
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="h-[52px] w-[250px] border-t bg-white px-4">
                {t('monitored')}
              </TableCell>
              {dataDistrict.map((district) => {
                const data = getCottonByDistrictSoato(district?.district_soato);
                return (
                  <TableCell
                    key={district?.district_soato}
                    className="h-[52px] w-[140px] text-center !px-1 bg-white"
                  >
                    {plantId === '1'
                      ? Number(data?.grain_plan || 0)?.toFixed(2)
                      : plantId === '2'
                        ? Number(data?.cotton_plan || 0).toFixed(2)
                        : '-'}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableBody>
        </table>
      </div>

      {/* Scrollable Body Table */}
      <div
        ref={bodyScrollRef}
        className={cn(
          'flex-1 overflow-auto',
          open ? 'w-[calc(100vw-320px)]' : 'w-[calc(100vw-150px)]',
        )}
      >
        <table
          className="w-full caption-bottom text-sm border-collapse table-fixed"
          style={{ width: 'max-content', minWidth: '100%' }}
        >
          {/* Header to maintain column widths */}
          <TableHeader className="invisible h-0">
            <TableRow className="h-0">
              <TableHead className="h-0 w-[250px] py-0 border-0 px-4"></TableHead>
              {dataDistrict?.map((district) => (
                <TableHead
                  key={district?.id}
                  className="h-0 w-[140px] py-0 border-0 !px-1"
                ></TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {allMonths(dataDistrict)?.map((month) => (
              <React.Fragment key={month}>
                <TableRow className="bg-gray-100 hover:bg-gray-100">
                  <TableCell
                    colSpan={dataDistrict.length + 1}
                    className="h-[42px] border-t font-medium px-4"
                  >
                    {t(`${MONTHS.find((item) => item.value === month)?.value}`)}
                  </TableCell>
                </TableRow>
                {getActionsByMonth(month, dataDistrict).map((action, index) => {
                  const {
                    action_name: actionName,
                    planting_type: plantingType,
                  } = action;
                  const firstDistrict = dataDistrict[0];
                  const firstAction = firstDistrict?.inspected_area?.find(
                    (item) =>
                      item.month === month &&
                      item.action_name === actionName &&
                      (item.planting_type || '') === (plantingType || ''),
                  );
                  const inspectedArea = Number(
                    firstAction?.inspected_area || 0,
                  ).toFixed(2);

                  return (
                    <TableRow
                      key={`${month}-${actionName}-${plantingType || ''}`}
                      className="hover:bg-light border-b border-gray-100"
                    >
                      <TableCell className="min-h-[44px] w-[250px] py-2 px-4 leading-tight whitespace-normal break-words">
                        <div>
                          {`${index + 1}. ${actionName}`}{' '}
                          {month === 'APRIL' &&
                          actionName === 'Chigit ekish' &&
                          year === '2025' &&
                          plantId === '2' ? (
                            <span className="font-semibold">
                              ({t('total')})
                            </span>
                          ) : (
                            ''
                          )}
                        </div>
                        {plantingType && (
                          <div className="text-[12px] text-gray-400 mt-0.5">
                            {t(plantingType)}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="h-[44px] w-[140px] text-center !px-1">
                        {inspectedArea}
                      </TableCell>
                      {dataDistrict.slice(1).map((district) => (
                        <TableCell
                          key={district?.name_uz}
                          className="h-[44px] w-[140px] text-center !px-1"
                        >
                          {getValue(
                            district ? district : undefined,
                            month,
                            actionName,
                            plantingType,
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </React.Fragment>
            ))}
          </TableBody>
        </table>
      </div>
    </div>
  );
};
